// The thread UI: a tabbed view (chat + post-search results). The sole thread
// implementation — ChatView renders it directly.

import {Box, Stack} from "@mantine/core";
import UserMessage from "./UserMessage";
import AssistantMessage from "./AssistantMessage";
import {ExperimentalEngineIndicator} from "../components/ExperimentalEngineIndicator";
import {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {useSearchParams} from "react-router-dom";
import classes from "../components/scrollContainer.module.css";
import threadClasses from "./Thread.module.css";
import {useAppSelector} from "../../../app/hooks";
import {bffApi} from "../bffApi";
import {ChatInput} from "./ChatInput";
import {useChatScroll} from "../hooks/useChatScroll";
import {useThreadArtifacts} from "../hooks/useThreadArtifacts";
import {useMessageHeightTracking} from "../hooks/useMessageHeightTracking";
import {MessageWithMetadata} from "../../../types";
import {ChatViewContext} from "./ChatView";
import {
    selectActiveThread,
    selectActiveThreadMessages,
    selectIsActiveThreadStreaming,
} from "../threadsSlice";
import {InterruptedTurn} from "../components/error-handling/InterruptedTurn";
import {ConversationLimitReached} from "../components/error-handling/ConversationLimitReached";
import {TopBarSlot} from "../TopBarContext";
import {ThreadTabs, ThreadTabKey} from "../components/ThreadTabs";
import {PostSearchTabPanel} from "../components/PostSearchTabPanel";

type ThreadProps = {
    id?: string;
    chatDisabled: boolean;
};

const chatInputEstimatedHeight = 7;
const MESSAGE_LIMIT = 50;

export const Thread = ({
    id,
    chatDisabled,
}: ThreadProps) => {
    const userData = bffApi.endpoints.getUser.useQuery();
    const messages = useAppSelector(selectActiveThreadMessages);
    const activeThread = useAppSelector(selectActiveThread);
    const threadName = activeThread?.name;
    const isStreaming = useAppSelector(selectIsActiveThreadStreaming);
    const ephemeralSearch = activeThread?.ephemeralSearch ?? null;

    // Persist tab selection in the URL so back/forward navigation (e.g. after
    // clicking a search result) restores the previously viewed tab.
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const tabFromUrl: ThreadTabKey =
        tabParam === "tools" || tabParam === "sources" ? tabParam : "answer";
    const setActiveTab = useCallback(
        (tab: ThreadTabKey) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (tab === "answer") {
                        next.delete("tab");
                    } else {
                        next.set("tab", tab);
                    }
                    return next;
                },
                {replace: true}
            );
        },
        [setSearchParams]
    );
    const activeTab: ThreadTabKey = tabFromUrl;
    const postSearch = activeThread?.postSearch ?? null;
    const isSearching = postSearch?.status === "searching";
    const toolsCount = postSearch?.tools.length ?? 0;
    const sourcesCount = postSearch?.otherSources.length ?? 0;
    // While searching, show both tabs with loading spinners; once results are
    // ready, show only the lists that have items.
    const showToolsTab =
        isSearching || (postSearch?.status === "ready" && toolsCount > 0);
    const showSourcesTab =
        isSearching || (postSearch?.status === "ready" && sourcesCount > 0);

    // If the active search tab disappears (new turn, error, empty list, etc.)
    // snap back to the answer tab.
    useEffect(() => {
        if (
            (activeTab === "tools" && !showToolsTab) ||
            (activeTab === "sources" && !showSourcesTab)
        ) {
            setActiveTab("answer");
        }
    }, [showToolsTab, showSourcesTab, activeTab]);

    // When the assistant finishes a response while the user is on the search
    // tab, surface a "answer ready" badge on the answer tab. It clears as soon
    // as they switch to the answer tab.
    const [hasAnswerBadge, setHasAnswerBadge] = useState(false);
    const wasStreamingRef = useRef(isStreaming);
    useEffect(() => {
        const wasStreaming = wasStreamingRef.current;
        wasStreamingRef.current = isStreaming;
        if (wasStreaming && !isStreaming && activeTab !== "answer") {
            setHasAnswerBadge(true);
        }
    }, [isStreaming, activeTab]);
    useEffect(() => {
        if (activeTab === "answer" && hasAnswerBadge) {
            setHasAnswerBadge(false);
        }
    }, [activeTab, hasAnswerBadge]);
    const isContentFiltered =
        activeThread?.error?.message?.includes("content_filter") ?? false;
    const [animatingPosition, setAnimatingPosition] = useState<number | null>(
        null
    );
    const userMessageCount =
        messages?.filter((m: MessageWithMetadata) => m.role === "user")
            .length ?? 0;
    const {
        scrollRef: viewport,
        isInitial,
        showScrollDownButton,
        forceScrollToBottom,
    } = useChatScroll([messages, animatingPosition], {
        disableAutoScroll: !!ephemeralSearch,
        isStreaming,
    });
    const last = (messages?.length ?? 0) - 1;

    const {containerRef, lastUserMessageRef, heightsMeasured} =
        useMessageHeightTracking(messages, chatInputEstimatedHeight);

    const {threadArtifacts, artifactCardMap} = useThreadArtifacts(messages);

    useEffect(() => {
        if (userMessageCount > 1 && !isStreaming && !ephemeralSearch) {
            viewport.current?.scrollTo(0, viewport.current?.scrollHeight);
        }
    }, [viewport.current, userMessageCount, ephemeralSearch]);

    useEffect(() => {
        if (threadName) {
            document.title = threadName;
        }

        return () => {
            document.title = "KAI";
        };
    }, [threadName]);

    const handleAnimationStart = useCallback((position: number) => {
        setAnimatingPosition(position);
    }, []);

    const handleAnimationFinish = useCallback((position: number) => {
        setAnimatingPosition((current) =>
            current === position ? null : current
        );
    }, []);

    const {sendMessage, resend} = useContext(ChatViewContext);
    const handleSendMessage = useCallback(
        (message: string) => {
            sendMessage(message);
            requestAnimationFrame(() => {
                forceScrollToBottom();
            });
        },
        [sendMessage, forceScrollToBottom]
    );

    return (
        <>
            <TopBarSlot slot="center">
                <ThreadTabs
                    active={activeTab}
                    onChange={setActiveTab}
                    toolsCount={toolsCount}
                    sourcesCount={sourcesCount}
                    searchLoading={postSearch?.status === "searching"}
                    showToolsTab={showToolsTab}
                    showSourcesTab={showSourcesTab}
                    showAnswerBadge={hasAnswerBadge}
                />
            </TopBarSlot>
            <Box
                ref={containerRef}
                className={threadClasses.viewport}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <div
                    className={threadClasses.panel}
                    data-active={
                        activeTab === "answer" ||
                        (activeTab === "tools" && !showToolsTab) ||
                        (activeTab === "sources" && !showSourcesTab) ||
                        (!showToolsTab && !showSourcesTab)
                    }
                >
                    <Box
                        ref={viewport}
                        className={`${classes.scrollbox} ${
                            !isInitial ? classes.smoothScroll : ""
                        }`}
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            position: "relative",
                            paddingBottom: `${chatInputEstimatedHeight}rem`,
                            scrollPaddingBottom: `${chatInputEstimatedHeight}rem`,
                            marginBottom: `-${chatInputEstimatedHeight + 2}rem`,
                        }}
                        data-name="thread"
                    >
                        <Stack
                            className={threadClasses.threadbox}
                            justify="flex-start"
                            align="stretch"
                        >
                            <ExperimentalEngineIndicator />
                            {messages?.map(
                                (msg: MessageWithMetadata, i: number) => {
                                    if (msg.role === "user") {
                                        return (
                                            <UserMessage
                                                ref={
                                                    i === last - 1
                                                        ? lastUserMessageRef
                                                        : null
                                                }
                                                key={`${id || "thread"}-user-${msg.position}`}
                                                message={msg}
                                                initials={
                                                    userData.data?.initials ??
                                                    "U"
                                                }
                                                forceShowEdit={
                                                    isContentFiltered &&
                                                    i === last - 1
                                                }
                                            />
                                        );
                                    }
                                    return (
                                        <div
                                            key={`${id || "thread"}-assistant-${msg.position}`}
                                        >
                                            <AssistantMessage
                                                message={msg}
                                                isStreaming={
                                                    isStreaming && i === last
                                                }
                                                onAnimationStart={
                                                    handleAnimationStart
                                                }
                                                onAnimationFinish={
                                                    handleAnimationFinish
                                                }
                                                isLastMessageInThread={
                                                    i === last
                                                }
                                                heightsMeasured={
                                                    i === last
                                                        ? heightsMeasured
                                                        : undefined
                                                }
                                                threadArtifacts={
                                                    threadArtifacts
                                                }
                                                artifactCards={artifactCardMap.get(
                                                    msg.position
                                                )}
                                            />
                                        </div>
                                    );
                                }
                            )}
                            {(() => {
                                const lastMsg = messages?.[last];
                                if (
                                    !lastMsg ||
                                    lastMsg.role !== "user" ||
                                    isStreaming
                                ) {
                                    return null;
                                }
                                return (
                                    <InterruptedTurn
                                        onRetry={() =>
                                            resend(
                                                lastMsg.content,
                                                lastMsg.position
                                            )
                                        }
                                    />
                                );
                            })()}
                        </Stack>
                    </Box>
                    {(messages?.length ?? 0) >= MESSAGE_LIMIT ? (
                        <ConversationLimitReached />
                    ) : (
                        <ChatInput
                            disabled={chatDisabled}
                            onSubmit={handleSendMessage}
                            scrollRef={viewport}
                            showScrollDownButton={showScrollDownButton}
                        />
                    )}
                </div>
                {showToolsTab && (
                    <div
                        className={threadClasses.panel}
                        data-active={activeTab === "tools"}
                    >
                        <PostSearchTabPanel state={postSearch} kind="tools" />
                    </div>
                )}
                {showSourcesTab && (
                    <div
                        className={threadClasses.panel}
                        data-active={activeTab === "sources"}
                    >
                        <PostSearchTabPanel state={postSearch} kind="sources" />
                    </div>
                )}
            </Box>
        </>
    );
};
